// client/src/components/pages/LexiStar/StatusBoard/ControlRoom.tsx
//
// The control room (ST-00253): the status board's at-a-glance view (?view=control) and its
// wall display (?view=control&wall=1). Every truck in one colour-coded group (controlRoom.ts),
// the load cycle across the top — To source → At source → Travelling full → At destination →
// Travelling empty — and the trucks that need a look or stand idle underneath: Delayed, Parked
// (per geofence), In maintenance (time so far, ticking), Offline / no data, Unallocated.
//
// Each group: a large count, its sub-groups (per site / zone / kind) with their own counts, and
// a chip per truck — registration, driver, time in state, the trip's route, % of the route and
// the shared asset marker (ST-00244): a disc with its nose on the heading while the truck
// travels, a plain disc otherwise, hollow without a live fix. Colour is never the only signal:
// every group has an icon and a label, every chip its words. A chip opens the trip (else the truck's row); a
// group's header (or a sub-group's) opens the table filtered to exactly those trucks.
//
// The wall display: full screen, dark by default (?theme=light for a bright room), large type,
// nothing behind a hover, the screen kept awake, a clock and the data's age always in view. The
// board keeps refreshing itself (the status board's 30 s poll and live events).
//
// Phone: the totals, then the groups stacked with their counts first; a group opens in place.
//
// ST-00249: with onTruckAction (the user holds lexistar.maintenance.manage) every registered
// truck's chip gets a small "…" button beside it — Report breakdown / Book maintenance — that
// opens the maintenance dialog on that truck (not on the wall display: nothing to press there).
// An open breakdown / planned maintenance puts the truck in its In maintenance sub-group with its
// time so far, ticking; an open delay in Delayed (controlRoom.ts).
//
// ST-00276 phase 2 (map colours & states): a chip's colour is the truck's `display` (the
// organisation's display rules, evaluated by the server), and its overlay marks show on it — a
// ring / pulse around the marker, and a tag (glyph + words) per mark (map/DisplayMarks); the
// default rules' own two marks keep their existing tags (driver reminded, position age).

import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  FaCarCrash,
  FaChevronDown,
  FaChevronRight,
  FaCompress,
  FaEllipsisV,
  FaExclamationTriangle,
  FaExpand,
  FaMoon,
  FaSun,
  FaTimes,
  FaWrench,
} from 'react-icons/fa';
import { AssetMarkerIcon, assetMarkerKind } from '../../../map/assetMarker';
import { hasOverlay } from '../../../map/assetVisualRules';
import { extraMarks } from '../../../map/assetDisplay';
import DisplayMarks from '../../../map/DisplayMarks';
import { SegmentedControl, ThemedSelect } from '../../../../types/commonComponents';
import { MINING_TZ } from '../../../../utils/dateUtils';
import { AckRemindersTag, AwaitingReportTag, ConnectionIndicator } from './atoms';
import { rowDriver, rowRegistration } from './columns';
import { dataAge } from './format';
import { TONES } from './statusLanguage';
import {
  BreakdownIcon,
  CONTROL_TONE_STYLES,
  buildControlBoard,
  controlToneMarker,
  fmtElapsed,
  scopeIsSet,
  type ControlBoard,
  type ControlClass,
  type ControlGroupId,
  type ControlGroupView,
  type ControlScope,
  type ControlTotals,
  type PlacedRow,
  type ScopeOption,
  type scopeOptions,
} from './controlRoom';
import type { StatusBoardRow } from '../../../../types/lexistar-status-board';

export type ScopeOptions = ReturnType<typeof scopeOptions>;
export type WallTheme = 'dark' | 'light';
/** ST-00249: what a truck chip's "…" menu offers. */
export type TruckAction = 'breakdown' | 'maintenance';
export type OnTruckAction = (row: StatusBoardRow, action: TruckAction) => void;

const TRAVELLING: ControlGroupId[] = ['to_source', 'loaded', 'empty'];

/** How old the board's data may get before the strip warns (the poll is 30 s). */
const DATA_AGING_S = 75;
const DATA_STALE_S = 300;

// ── Clock ───────────────────────────────────────────────────────────────────

const clockFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: MINING_TZ,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});
const dayFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: MINING_TZ,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/** A clock that ticks every second while `enabled` (the wall clock and the maintenance timers). */
function useSecondClock(enabled = true): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [enabled]);
  return now;
}

// ── Pieces ──────────────────────────────────────────────────────────────────

/** The data's age as the strip says it: fine, aging (amber) or stale (red). */
function dataFreshness(fetchedAt: number | null, now: number, error: boolean) {
  if (fetchedAt === null) return { level: error ? 'stale' : 'fresh', text: error ? 'Not loaded yet' : 'Loading…' } as const;
  const s = Math.max(0, Math.floor((now - fetchedAt) / 1000));
  const text = s < 60 ? `${s} s ago` : `${Math.floor(s / 60)} min ago`;
  if (s >= DATA_STALE_S) return { level: 'stale', text: `Data ${Math.floor(s / 60)} min old — the board is not updating` } as const;
  if (s >= DATA_AGING_S || error) return { level: 'aging', text: `Updated ${text}` } as const;
  return { level: 'fresh', text: `Updated ${text}` } as const;
}

interface StatProps {
  label: string;
  /** null before the first load ("—"). */
  value: number | null;
  of?: number;
  wall: boolean;
  tone?: 'maintenance' | 'offline';
  className?: string;
}

function Stat({ label, value, of, wall, tone, className = '' }: StatProps) {
  const pct = value !== null && of && of > 0 ? Math.round((value / of) * 100) : null;
  const valueTone =
    tone && (value ?? 0) > 0 ? CONTROL_TONE_STYLES[tone].count : 'text-slate-900';
  return (
    <div
      role="group"
      aria-label={`${label}: ${value ?? 'not loaded yet'}${pct !== null ? ` (${pct}%)` : ''}`}
      className={`flex flex-col justify-center min-w-0 ${wall ? 'px-4 2xl:px-6 py-3' : 'px-4 py-2.5'} ${className}`}
    >
      <span className={`${wall ? 'text-sm 2xl:text-base' : 'text-xs'} font-medium text-slate-600 whitespace-nowrap`}>{label}</span>
      <span className="flex items-baseline gap-2">
        <span className={`${wall ? 'text-4xl 2xl:text-5xl' : 'text-3xl'} font-bold tabular-nums leading-tight ${valueTone}`}>
          {value ?? '—'}
        </span>
        {pct !== null && <span className={`${wall ? 'text-lg 2xl:text-xl' : 'text-sm'} font-semibold tabular-nums text-slate-500`}>{pct}%</span>}
      </span>
    </div>
  );
}

export interface TotalsStripProps {
  /** The wall's title, before the totals. */
  lead?: ReactNode;
  /** The wall's buttons, after the clock. */
  trail?: ReactNode;
  totals: ControlTotals;
  /** false before the first load: the figures read "—", not 0. */
  ready?: boolean;
  now: number;
  fetchedAt: number | null;
  error: boolean;
  connection: string | null;
  wall: boolean;
  phone?: boolean;
}

/** Fleet · Working · Idle · In maintenance · Offline, with the clock and the data's age. */
export function TotalsStrip({ lead, trail, totals: figures, ready = true, now, fetchedAt, error, connection, wall, phone = false }: TotalsStripProps) {
  const v = (n: number): number | null => (ready ? n : null);
  const totals = { fleet: v(figures.fleet), working: v(figures.working), idle: v(figures.idle), maintenance: v(figures.maintenance), offline: v(figures.offline) };
  const fresh = dataFreshness(fetchedAt, now, error);
  const freshClass =
    fresh.level === 'stale'
      ? 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-300'
      : fresh.level === 'aging'
        ? 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-300'
        : 'text-slate-600';
  const stats = (
    <>
      <Stat label="Fleet" value={totals.fleet} wall={wall} />
      <Stat label="Working" value={totals.working} of={figures.fleet} wall={wall} />
      <Stat label="Idle" value={totals.idle} wall={wall} />
      <Stat label="In maintenance" value={totals.maintenance} wall={wall} tone="maintenance" />
      <Stat label="Offline" value={totals.offline} wall={wall} tone="offline" />
    </>
  );
  const clock = (
    <div className={`flex items-center gap-4 ${phone ? 'px-4 py-2 justify-between' : wall ? 'px-4 2xl:px-6' : 'px-4'} min-w-0`}>
      <div className="flex flex-col items-end min-w-0" data-testid="control-clock">
        <span className={`${wall ? 'text-4xl 2xl:text-5xl' : phone ? 'text-xl' : 'text-2xl'} font-bold tabular-nums text-slate-900 leading-tight`}>
          {clockFormat.format(now)}
        </span>
        <span className={`${wall ? 'text-sm 2xl:text-base' : 'text-xs'} text-slate-600 whitespace-nowrap`}>{dayFormat.format(now)} · SAST</span>
      </div>
      <div className="flex flex-col items-end gap-1 min-w-0">
        <ConnectionIndicator status={connection} />
        <span
          className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 ${wall ? 'text-sm 2xl:text-base' : 'text-xs'} font-medium tabular-nums whitespace-nowrap ${freshClass}`}
          data-freshness={fresh.level}
        >
          {fresh.level !== 'fresh' && <FaExclamationTriangle aria-hidden="true" className="flex-shrink-0" />}
          {fresh.text}
        </span>
      </div>
    </div>
  );
  if (phone) {
    return (
      <div className="bg-surface rounded-xl border border-slate-200 shadow-sm overflow-hidden" aria-label="Fleet totals">
        <div className="grid grid-cols-6 gap-px bg-slate-200 border-b border-slate-200">
          <Stat label="Fleet" value={totals.fleet} wall={false} className="col-span-3 bg-surface !px-3" />
          <Stat label="Working" value={totals.working} of={figures.fleet} wall={false} className="col-span-3 bg-surface !px-3" />
          <Stat label="Idle" value={totals.idle} wall={false} className="col-span-2 bg-surface !px-3" />
          <Stat label="Maintenance" value={totals.maintenance} wall={false} tone="maintenance" className="col-span-2 bg-surface !px-3" />
          <Stat label="Offline" value={totals.offline} wall={false} tone="offline" className="col-span-2 bg-surface !px-3" />
        </div>
        {clock}
      </div>
    );
  }
  return (
    <div
      className={`flex ${wall ? 'flex-nowrap' : 'flex-wrap'} items-stretch justify-between bg-surface rounded-xl border border-slate-200 shadow-sm`}
      aria-label="Fleet totals"
    >
      <div className={`flex ${wall ? 'flex-nowrap min-w-0' : 'flex-wrap'} items-stretch divide-x divide-slate-200`}>
        {lead}
        {stats}
      </div>
      <div className="flex items-center">
        {clock}
        {trail}
      </div>
    </div>
  );
}

const pctText = (pct: number | null | undefined): string | null =>
  pct === null || pct === undefined || !Number.isFinite(Number(pct)) ? null : `${Math.round(Number(pct) * 100)}%`;

const etaDeltaText = (min: number | null | undefined): string | null => {
  if (min === null || min === undefined || !Number.isFinite(min) || Math.abs(min) < 5) return null;
  return min > 0 ? `${Math.round(min)} min late` : `${Math.round(-min)} min early`;
};

interface ChipProps {
  pr: PlacedRow;
  group: ControlGroupId;
  now: number;
  wall: boolean;
  onOpen: (row: StatusBoardRow) => void;
  /** ST-00249: keep the lines under the first clear of the "…" button in the bottom-right corner. */
  reserve?: boolean;
}

/** The maintenance kind's icon on a chip (breakdown's own, else the spanner). */
function maintenanceIcon(tone: string, className: string): ReactNode {
  const Icon = tone === 'breakdown' ? TONES.breakdown.Icon : TONES.maintenance.Icon;
  return <Icon aria-hidden="true" className={`flex-shrink-0 ${className}`} />;
}

/** Where the trip is taking the truck, as the group needs it (the sub-group already names the site). */
function routeText(group: ControlGroupId, trip: StatusBoardRow['trip']): string | null {
  if (!trip) return null;
  const from = trip.from_name || null;
  const to = trip.to_name || null;
  switch (group) {
    case 'to_source':
      return from ? `to ${from}` : null;
    case 'at_source':
      return to ? `for ${to}` : null;
    case 'loaded':
      return to ? `to ${to}` : null;
    case 'at_destination':
      return from ? `from ${from}` : null;
    case 'empty':
      return to ? `from ${to}` : null;
    default:
      return from || to ? `${from || '—'} → ${to || '—'}` : null;
  }
}

/** One truck: registration, time in state, driver, where to, progress — all visible, no hover. */
function TruckChip({ pr, group, now, wall, onOpen, reserve = false }: ChipProps) {
  const { row, place } = pr;
  const style = CONTROL_TONE_STYLES[place.tone];
  const reg = rowRegistration(row);
  const driver = rowDriver(row);
  const trip = row.trip;
  const travelling = TRAVELLING.includes(group);
  const inStateS = place.since ? Math.max(0, Math.floor((now - new Date(place.since).getTime()) / 1000)) : null;
  // Maintenance: the time so far on a line of its own, ticking by the second (ST-00253).
  const live = group === 'maintenance';
  const duration = live ? fmtElapsed(inStateS, { seconds: true }) : fmtElapsed(inStateS, { compact: !wall });
  const age = dataAge(row.position?.ts || row.last_update || null, now) as { text: string; level: string };
  // A position older than 15 minutes (LS-09 data age "stale"): hollow icon and a red tag — not
  // for a parked truck or one in maintenance, whose tracker reports seldom with the ignition off.
  const quiet = group === 'parked' || group === 'maintenance';
  const stale = group === 'offline' || (!quiet && age.level === 'stale');
  const pct = travelling ? trip?.pct_distance ?? null : null;
  const pctLabel = pctText(pct);
  const late = travelling || group === 'delayed' ? etaDeltaText(trip?.eta_delta_min) : null;
  const route = routeText(group, trip);
  const reason =
    group === 'maintenance'
      ? row.maintenance?.reason || row.status?.override?.reason || null
      : group === 'delayed' && row.delay
        ? row.delay.notes || null
        : null;
  const durationText = group === 'offline' && !place.since ? 'never' : `${place.sinceExact ? '' : '≥'}${duration}`;
  const label = [
    reg,
    driver || 'no driver',
    group === 'offline'
      ? place.since
        ? `last seen ${duration} ago`
        : 'no data yet'
      : `${place.sinceExact ? '' : 'at least '}${duration} in this state`,
    place.note,
    reason,
    route,
    pctLabel ? `${pctLabel} of the route` : null,
    late,
    stale && group !== 'offline' ? `position ${age.text}` : null,
    trip ? 'open the trip' : 'show the truck',
  ]
    .filter(Boolean)
    .join(', ');
  const small = `${wall ? 'text-base' : 'text-xs'}${reserve ? ' pr-6' : ''}`;
  // ST-00276: the marks of the organisation's own rules (the defaults' two have their own tags).
  const marks = extraMarks(place.display?.overlays).filter((o) => !BUILT_IN_MARKS.includes(o.rule_id));
  const ringMark = marks.find((o) => o.mark === 'ring' || o.mark === 'pulse');
  const tags =
    trip?.awaiting_first_report || (trip?.ack_reminders ?? 0) > 0 || (stale && group !== 'offline') || marks.length > 0;
  const marker = (
    <AssetMarkerIcon
      kind={assetMarkerKind(row.asset as { asset_type?: string | null; vehicle_class?: string | null } | null)}
      color={controlToneMarker(place.tone)}
      heading={travelling ? row.position?.heading ?? null : null}
      size={wall ? 26 : 20}
      hollow={stale || place.visual.hollow}
      className="flex-shrink-0"
    />
  );
  const icon = ringMark ? (
    <span
      className={`inline-flex flex-shrink-0 rounded-full${ringMark.mark === 'pulse' ? ' motion-safe:animate-pulse' : ''}`}
      style={{ boxShadow: `0 0 0 2px var(--as-${String(ringMark.token || 'parked').replace(/_/g, '-')}-solid)` }}
      data-icon-mark={ringMark.mark}
    >
      {marker}
    </span>
  ) : (
    marker
  );
  const chipClass = `w-full text-left bg-surface rounded-lg ${wall ? 'px-3 py-2' : 'px-2 py-1.5'} shadow-sm ring-1 ring-inset ring-slate-200 hover:ring-2 hover:ring-primary-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 transition-shadow duration-150`;

  if (wall) {
    // Two lines, read from across the room: what and how long; then the one thing that matters
    // most for this truck (a warning first, then lateness, then where to / how far, else who).
    const ackN = trip?.stage === 'DISPATCHED' ? Number(trip?.ack_reminders) || 0 : 0;
    const second: ReactNode = live ? (
      <>
        <span className={`flex-shrink-0 tabular-nums font-bold ${style.text}`} data-duration={inStateS ?? ''}>
          {durationText}
        </span>
        {(reason || place.note) && <span className="min-w-0 truncate text-slate-700">{reason || place.note}</span>}
      </>
    ) : trip?.awaiting_first_report ? (
      <span className="min-w-0 truncate font-semibold text-sky-800">waiting for first report</span>
    ) : ackN > 0 ? (
      <span className="min-w-0 truncate font-semibold text-amber-800">driver reminded ×{ackN}</span>
    ) : stale && group !== 'offline' ? (
      <span className="min-w-0 truncate font-semibold text-red-800">position {age.text}</span>
    ) : late && pct === null ? (
      <span className="min-w-0 truncate">
        <span className="font-semibold text-amber-800">{late}</span>
        {route && <span className="text-slate-600"> · {route}</span>}
      </span>
    ) : pct !== null ? (
      <>
        <span className={`min-w-0 flex-1 truncate ${late ? 'font-semibold text-amber-800' : 'text-slate-600'}`}>{late || route}</span>
        <span className="relative flex-shrink-0 w-10 2xl:w-16 h-2 rounded-full bg-slate-200 overflow-hidden" aria-hidden="true">
          <span className={`absolute inset-y-0 left-0 rounded-full ${style.bar}`} style={{ width: `${Math.min(100, Math.max(0, Number(pct) * 100))}%` }} />
        </span>
        <span className="flex-shrink-0 tabular-nums font-semibold text-slate-700">{pctLabel}</span>
      </>
    ) : (
      <span className="min-w-0 truncate text-slate-600">
        {driver || <span className="italic">No driver</span>}
        {place.note && <span className="font-medium text-slate-800"> · {place.note}</span>}
        {!place.note && route && group !== 'at_source' && group !== 'at_destination' ? ` · ${route}` : ''}
      </span>
    );
    return (
      <button type="button" onClick={() => onOpen(row)} aria-label={label} data-chip={row.asset_key} data-tone={place.tone} className={chipClass}>
        <span className="flex items-center gap-2">
          {icon}
          <span className="min-w-0 flex-1 truncate font-mono font-semibold tracking-tight text-slate-900 text-base 2xl:text-lg">{reg}</span>
          {live ? (
            maintenanceIcon(place.tone, style.text)
          ) : (
            <span className={`flex-shrink-0 tabular-nums font-bold text-base 2xl:text-lg ${style.text}`} data-duration={inStateS ?? ''}>
              {durationText}
            </span>
          )}
        </span>
        <span className="mt-0.5 flex items-center gap-2 text-sm 2xl:text-base">{second}</span>
        {marks.length > 0 && <DisplayMarks overlays={marks} large className="mt-1" />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onOpen(row)}
      aria-label={label}
      data-chip={row.asset_key}
      data-tone={place.tone}
      className={chipClass}
    >
      <span className="flex items-center gap-1.5">
        {icon}
        <span className={`min-w-0 flex-1 truncate font-mono font-semibold tracking-tight text-slate-900 ${wall ? 'text-lg' : 'text-[13px]'}`}>
          {reg}
        </span>
        {live ? (
          maintenanceIcon(place.tone, style.text)
        ) : (
          <span className={`flex-shrink-0 tabular-nums font-bold ${style.text} ${wall ? 'text-lg' : 'text-[13px]'}`} data-duration={inStateS ?? ''}>
            {durationText}
          </span>
        )}
      </span>
      {live && (
        <span className={`block tabular-nums font-bold leading-tight ${style.text} ${wall ? 'text-2xl' : 'text-base'}${reserve ? ' pr-6' : ''}`} data-duration={inStateS ?? ''}>
          {durationText}
        </span>
      )}
      <span className={`mt-0.5 block truncate text-slate-600 ${small}`}>
        {driver || <span className="italic">No driver</span>}
        {place.note && <span className="font-medium text-slate-800"> · {place.note}</span>}
        {late && <span className={late.endsWith('late') ? 'font-semibold text-amber-800' : ''}> · {late}</span>}
      </span>
      {reason && <span className={`block truncate font-medium text-slate-800 ${small}`}>{reason}</span>}
      {(route || pct !== null) && (
        <span className={`flex items-center gap-1.5 ${small}`}>
          {route && <span className="min-w-0 flex-1 truncate text-slate-600">{route}</span>}
          {pct !== null && (
            <>
              <span className={`relative flex-shrink-0 ${wall ? 'w-16 h-2' : 'w-10 h-1.5'} rounded-full bg-slate-200 overflow-hidden`} aria-hidden="true">
                <span className={`absolute inset-y-0 left-0 rounded-full ${style.bar}`} style={{ width: `${Math.min(100, Math.max(0, Number(pct) * 100))}%` }} />
              </span>
              <span className="flex-shrink-0 tabular-nums font-semibold text-slate-700">{pctLabel}</span>
            </>
          )}
        </span>
      )}
      {tags && (
        <span className={`mt-1 flex flex-wrap items-center gap-1${reserve ? ' pr-6' : ''}`}>
          <AwaitingReportTag trip={trip} />
          {/* The rules' "driver reminded" overlay (map/assetVisualRules). */}
          {hasOverlay(place.visual, 'driver-reminded') && <AckRemindersTag trip={trip} />}
          {stale && group !== 'offline' && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold whitespace-nowrap bg-red-50 text-red-800 ring-1 ring-inset ring-red-200">
              position {age.text}
            </span>
          )}
          <DisplayMarks overlays={marks} />
        </span>
      )}
    </button>
  );
}

/** The default rules' marks the chips draw their own way (AckRemindersTag; the hollow marker + age). */
const BUILT_IN_MARKS = ['driver-reminded', 'no-recent-position'];

/** ST-00249: the "…" beside a chip — Report breakdown / Book maintenance on that truck. */
function TruckActions({ row, onAction, side = false }: { row: StatusBoardRow; onAction: OnTruckAction; side?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const reg = rowRegistration(row);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  const choose = (a: TruckAction) => {
    setOpen(false);
    onAction(row, a);
  };
  const item =
    'w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-100 focus-visible:outline-none focus-visible:bg-slate-100';
  return (
    <div ref={ref} className={side ? 'relative flex-shrink-0 flex' : 'absolute bottom-1 right-1'}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Maintenance for ${reg}`}
        data-truck-actions={row.asset_key}
        className={`${
          side ? 'touch-target w-11 rounded-lg bg-surface ring-1 ring-inset ring-slate-200' : 'w-6 h-6 rounded-md hover:bg-slate-100'
        } flex items-center justify-center text-slate-500 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
      >
        <FaEllipsisV size={11} aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" aria-label={`Maintenance for ${reg}`} className="absolute right-0 top-full mt-1 z-30 w-48 rounded-lg bg-surface shadow-lg ring-1 ring-slate-200 py-1">
          <button type="button" role="menuitem" onClick={() => choose('breakdown')} className={item}>
            <FaCarCrash className={CONTROL_TONE_STYLES.breakdown.text} aria-hidden="true" /> Report breakdown
          </button>
          <button type="button" role="menuitem" onClick={() => choose('maintenance')} className={item}>
            <FaWrench className={CONTROL_TONE_STYLES.planned_maintenance.text} aria-hidden="true" /> Book maintenance
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * A chip, with its "…" maintenance menu in the bottom-right corner when the user may book (never
 * on the wall). The button sits over the chip, not inside it (no button in a button); the lines
 * under the registration keep clear of it, so the registration and the time keep their width.
 */
function ChipSlot(props: ChipProps & { onTruckAction?: OnTruckAction; side?: boolean }) {
  const { onTruckAction, side = false, ...chip } = props;
  if (chip.wall || !onTruckAction || !chip.pr.row.asset_id) return <TruckChip {...chip} />;
  // Phone (full-width chips, thumbs): a 44 px button beside the chip instead.
  if (side) {
    return (
      <div className="flex items-stretch gap-1.5">
        <div className="flex-1 min-w-0">
          <TruckChip {...chip} />
        </div>
        <TruckActions row={chip.pr.row} onAction={onTruckAction} side />
      </div>
    );
  }
  return (
    <div className="relative">
      <TruckChip {...chip} reserve />
      <TruckActions row={chip.pr.row} onAction={onTruckAction} />
    </div>
  );
}

export interface GroupPanelProps {
  view: ControlGroupView;
  now: number;
  wall: boolean;
  /** Chips shown before "+N more". */
  limit: number;
  onOpenRow: (row: StatusBoardRow) => void;
  onOpenGroup: (group: ControlGroupId, subKey?: string | null) => void;
  /** The cycle's arrow to the next group. */
  next?: boolean;
  className?: string;
  /** ST-00249: the chips' maintenance menu (lexistar.maintenance.manage). */
  onTruckAction?: OnTruckAction;
}

/**
 * The wall display shows as many whole chips as the panel holds (never one cut in half): after
 * each render the content is measured against the panel's body and the count moves by one
 * until it fits — re-run when the panel resizes or its trucks change.
 */
function useWallFit(
  enabled: boolean,
  body: RefObject<HTMLDivElement | null>,
  inner: RefObject<HTMLDivElement | null>,
  total: number,
  initial: number
): number {
  const [fit, setFit] = useState(initial);
  const [size, setSize] = useState(0);
  // Once a step down was needed for this size and fleet, never step back up (no flip-flop).
  const shrunkFor = useRef<string | null>(null);
  const key = `${size}|${total}`;
  useEffect(() => {
    if (!enabled || !body.current || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setSize((n) => n + 1));
    ro.observe(body.current);
    return () => ro.disconnect();
  }, [enabled, body]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- measures after every render on purpose; settles in a step or two (see shrunkFor)
  useLayoutEffect(() => {
    if (!enabled || !body.current || !inner.current) return;
    const room = body.current.clientHeight;
    const used = inner.current.offsetHeight;
    if (used > room + 1 && fit > 0) {
      shrunkFor.current = key;
      setFit((n) => n - 1);
    } else if (fit < total && shrunkFor.current !== key) {
      const chip = inner.current.querySelector<HTMLElement>('[data-chip]');
      const step = (chip?.offsetHeight ?? 88) + 8;
      if (room - used >= step) setFit((n) => n + 1);
    }
  });
  return Math.min(fit, total);
}

/** A group: icon, label, big count; its sub-groups with counts; its chips. */
export const GroupPanel = memo(function GroupPanel({ view, now, wall, limit: limitProp, onOpenRow, onOpenGroup, next = false, className = '', onTruckAction }: GroupPanelProps) {
  const { def, rows, subs } = view;
  const count = rows.length;
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const innerRef = useRef<HTMLDivElement | null>(null);
  const fitted = useWallFit(wall, bodyRef, innerRef, count, Math.min(limitProp, 4));
  const limit = wall ? fitted : limitProp;
  const style = CONTROL_TONE_STYLES[def.tone];
  const empty = count === 0;
  const headingId = `control-group-${def.id}`;
  let shown = 0;
  const take = (list: PlacedRow[]): PlacedRow[] => {
    const room = Math.max(0, limit - shown);
    const out = list.slice(0, room);
    shown += out.length;
    return out;
  };
  const chips = (list: PlacedRow[]) => (
    <ul className={`grid gap-1.5 ${wall ? 'grid-cols-[repeat(auto-fill,minmax(min(15rem,100%),1fr))]' : 'grid-cols-1'}`}>
      {take(list).map((pr) => (
        <li key={pr.row.asset_key}>
          <ChipSlot pr={pr} group={def.id} now={now} wall={wall} onOpen={onOpenRow} onTruckAction={onTruckAction} />
        </li>
      ))}
    </ul>
  );
  const subBlocks: ReactNode[] = [];
  if (subs.length) {
    for (const sub of subs) {
      const subTone = sub.tone ? CONTROL_TONE_STYLES[sub.tone] : style;
      subBlocks.push(
        <div key={sub.key} className={wall ? 'space-y-1' : 'space-y-1.5'}>
          <button
            type="button"
            onClick={() => onOpenGroup(def.id, sub.key)}
            className={`w-full flex items-center justify-between gap-2 rounded-md px-1 py-0.5 text-left hover:bg-surface/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${wall ? 'text-sm 2xl:text-base' : 'text-sm'}`}
            aria-label={`${def.label}, ${sub.label}: ${sub.rows.length} — show them in the table`}
            data-sub={sub.key}
          >
            <span className="inline-flex items-center gap-1.5 min-w-0 font-semibold text-slate-800">
              {sub.tone === 'breakdown' && <BreakdownIcon aria-hidden="true" className={subTone.text} />}
              <span className="truncate">{sub.label}</span>
            </span>
            <span className={`flex-shrink-0 tabular-nums font-bold ${subTone.count}`}>{sub.rows.length}</span>
          </button>
          {shown < limit && chips(sub.rows)}
        </div>
      );
    }
  }
  const hidden = count - Math.min(count, limit);
  return (
    <section
      aria-labelledby={headingId}
      data-group={def.id}
      data-count={count}
      className={`relative flex flex-col min-w-0 rounded-xl border ${empty ? 'bg-surface border-slate-200' : style.panel} ${className}`}
    >
      <h3 id={headingId} className="sr-only">
        {def.label}: {count}
      </h3>
      {wall ? (
        <button
          type="button"
          onClick={() => onOpenGroup(def.id)}
          className="flex items-center gap-3 w-full text-left rounded-t-xl px-4 pt-3 pb-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
          aria-label={`${def.label}: ${count} ${count === 1 ? 'truck' : 'trucks'} — show them in the table`}
        >
          <span
            className={`inline-flex items-center justify-center flex-shrink-0 rounded-lg w-9 h-9 text-lg 2xl:w-11 2xl:h-11 2xl:text-xl ${empty ? 'bg-slate-100 text-slate-500' : style.badge}`}
            aria-hidden="true"
          >
            <def.Icon />
          </span>
          <span className="flex-1 min-w-0">
            <span className="block text-xl 2xl:text-2xl font-semibold leading-tight text-slate-900">{def.label}</span>
            <span className="block truncate text-sm 2xl:text-base leading-snug text-slate-600">{def.hint}</span>
          </span>
          <span className={`flex-shrink-0 text-4xl xl:text-5xl 2xl:text-6xl font-bold tabular-nums leading-none ${empty ? 'text-slate-400' : style.count}`} data-group-count={def.id}>
            {count}
          </span>
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onOpenGroup(def.id)}
          className="block w-full text-left rounded-t-xl px-3 pt-3 pb-2.5 hover:bg-surface/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500"
          aria-label={`${def.label}: ${count} ${count === 1 ? 'truck' : 'trucks'} — show them in the table`}
        >
          <span className="flex items-center gap-2 min-w-0">
            <span
              className={`inline-flex items-center justify-center flex-shrink-0 rounded-lg w-7 h-7 text-sm ${empty ? 'bg-slate-100 text-slate-500' : style.badge}`}
              aria-hidden="true"
            >
              <def.Icon />
            </span>
            <span className="min-w-0 truncate text-sm font-semibold leading-tight text-slate-900">{def.label}</span>
          </span>
          <span className="mt-1.5 flex items-end justify-between gap-3">
            <span className={`text-5xl font-bold tabular-nums leading-none ${empty ? 'text-slate-400' : style.count}`} data-group-count={def.id}>
              {count}
            </span>
            <span className="min-w-0 max-w-[8.5rem] text-right text-xs leading-snug text-slate-600">{def.hint}</span>
          </span>
        </button>
      )}
      <div ref={bodyRef} className={`flex-1 min-h-0 ${wall ? 'px-4 pb-4 overflow-hidden' : 'px-2.5 pb-2.5'}`}>
        <div ref={innerRef} className={wall ? 'space-y-2' : 'space-y-2.5'}>
        {empty ? (
          <p className={`px-1 text-slate-500 ${wall ? 'text-lg' : 'text-xs'}`}>None right now</p>
        ) : subs.length ? (
          subBlocks
        ) : (
          chips(rows)
        )}
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => onOpenGroup(def.id)}
            className={`w-full rounded-lg border border-dashed border-slate-300 bg-surface/60 px-2 ${wall ? 'py-1 text-sm 2xl:text-base' : 'py-1.5 text-xs'} font-semibold text-slate-700 hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500`}
          >
            {wall ? `+${hidden} more` : `+${hidden} more — show all ${count} in the table`}
          </button>
        )}
        </div>
      </div>
      {next && (
        <span
          className="hidden xl:flex absolute -right-[0.95rem] top-6 z-10 w-6 h-6 items-center justify-center rounded-full bg-surface text-slate-500 ring-1 ring-slate-300"
          aria-hidden="true"
        >
          <FaChevronRight size={10} />
        </span>
      )}
    </section>
  );
});

// ── Scope ───────────────────────────────────────────────────────────────────

const CLASS_OPTIONS: Array<{ value: ControlClass; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'fleet', label: 'Fleet' },
  { value: 'loaders', label: 'Loaders' },
];

export interface ScopeBarProps {
  scope: ControlScope;
  options: ScopeOptions;
  onChange: (scope: ControlScope) => void;
}

/** Fleet / Loaders, site, route and customer (the customer only when loads carry one). */
export function ScopeBar({ scope, options, onChange }: ScopeBarProps) {
  const select = (
    id: string,
    label: string,
    value: string,
    list: ScopeOption[],
    all: string,
    key: 'site' | 'route' | 'customer'
  ) => (
    <div className="min-w-[11rem] max-w-[16rem] flex-1 sm:flex-none">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <ThemedSelect id={id} value={value} onChange={(e) => onChange({ ...scope, [key]: String(e.target.value) })} aria-label={label}>
        <option value="">{all}</option>
        {list.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </ThemedSelect>
    </div>
  );
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Control room scope">
      <SegmentedControl<ControlClass>
        options={CLASS_OPTIONS}
        value={scope.cls}
        onChange={(cls) => onChange({ ...scope, cls })}
        ariaLabel="Fleet or loaders"
      />
      {select('control-site', 'Site', scope.site, options.sites, 'All sites', 'site')}
      {select('control-route', 'Route', scope.route, options.routes, 'All routes', 'route')}
      {options.customers.length > 0 && select('control-customer', 'Customer', scope.customer, options.customers, 'All customers', 'customer')}
      {scopeIsSet(scope) && (
        <button
          type="button"
          onClick={() => onChange({ cls: 'all', site: '', route: '', customer: '' })}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        >
          <FaTimes size={10} aria-hidden="true" /> Clear
        </button>
      )}
    </div>
  );
}

/** The scope in words: "Loaders · Kromkrans Colliery" (the wall's header, the table's chip). */
export function scopeText(scope: ControlScope, options: ScopeOptions): string {
  const name = (list: ScopeOption[], v: string) => list.find((o) => o.value === v)?.label || v;
  return [
    scope.cls === 'fleet' ? 'Fleet' : scope.cls === 'loaders' ? 'Loaders' : null,
    scope.site ? name(options.sites, scope.site) : null,
    scope.route ? name(options.routes, scope.route) : null,
    scope.customer || null,
  ]
    .filter(Boolean)
    .join(' · ');
}

// ── Layouts ─────────────────────────────────────────────────────────────────

export interface ControlRoomProps {
  /** The board's rows inside the scope. */
  rows: StatusBoardRow[];
  /** The board's clock (ticks every few seconds). */
  now: number;
  scope: ControlScope;
  options: ScopeOptions;
  onScopeChange: (scope: ControlScope) => void;
  onOpenRow: (row: StatusBoardRow) => void;
  onOpenGroup: (group: ControlGroupId, subKey?: string | null) => void;
  fetchedAt: number | null;
  error: boolean;
  connection: string | null;
  loading: boolean;
  phone?: boolean;
  wall?: boolean;
  wallTheme?: WallTheme;
  onWallThemeChange?: (theme: WallTheme) => void;
  onEnterWall?: () => void;
  onExitWall?: () => void;
  /** ST-00249: Report breakdown / Book maintenance from a chip (lexistar.maintenance.manage). */
  onTruckAction?: OnTruckAction;
}

/** Desktop and wall: the cycle row, then the watch row. */
function GroupGrid({
  board,
  now,
  liveNow,
  wall,
  onOpenRow,
  onOpenGroup,
  onTruckAction,
}: {
  board: ControlBoard;
  now: number;
  liveNow: number;
  wall: boolean;
  onOpenRow: ControlRoomProps['onOpenRow'];
  onOpenGroup: ControlRoomProps['onOpenGroup'];
  onTruckAction?: OnTruckAction;
}) {
  const cycle = board.groups.filter((g) => g.def.lane === 'cycle');
  const watch = board.groups.filter((g) => g.def.lane === 'watch');
  const cycleLimit = 8;
  const watchLimit = wall ? 6 : 8;
  return (
    <div className={wall ? 'flex-1 min-h-0 grid grid-rows-2 gap-4' : 'space-y-6'}>
      <section aria-label="The load cycle" className={wall ? 'min-h-0' : ''}>
        {!wall && <h2 className="mb-2 text-sm font-semibold text-slate-700">The load cycle</h2>}
        <div className={`grid gap-4 ${wall ? 'grid-cols-5 h-full [&>section]:overflow-hidden' : 'grid-cols-1 md:grid-cols-3 xl:grid-cols-5 items-start'}`}>
          {cycle.map((g, i) => (
            <GroupPanel
              key={g.def.id}
              view={g}
              now={now}
              wall={wall}
              limit={cycleLimit}
              onOpenRow={onOpenRow}
              onOpenGroup={onOpenGroup}
              next={!wall && i < cycle.length - 1}
              onTruckAction={onTruckAction}
            />
          ))}
        </div>
      </section>
      <section aria-label="Delayed, parked, maintenance, offline and unallocated" className={wall ? 'min-h-0' : ''}>
        {!wall && <h2 className="mb-2 text-sm font-semibold text-slate-700">Needs a look, or standing idle</h2>}
        <div className={`grid gap-4 ${wall ? 'grid-cols-5 h-full [&>section]:overflow-hidden' : 'grid-cols-1 md:grid-cols-3 xl:grid-cols-5 items-start'}`}>
          {watch.map((g) => (
            <GroupPanel
              key={g.def.id}
              view={g}
              now={g.def.id === 'maintenance' ? liveNow : now}
              wall={wall}
              limit={watchLimit}
              onOpenRow={onOpenRow}
              onOpenGroup={onOpenGroup}
              onTruckAction={onTruckAction}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

/** Phone: the totals, then each group with its count first; tap to open it in place. */
function PhoneStack({
  board,
  now,
  liveNow,
  onOpenRow,
  onOpenGroup,
  onTruckAction,
}: {
  board: ControlBoard;
  now: number;
  liveNow: number;
  onOpenRow: ControlRoomProps['onOpenRow'];
  onOpenGroup: ControlRoomProps['onOpenGroup'];
  onTruckAction?: OnTruckAction;
}) {
  const [open, setOpen] = useState<ControlGroupId | null>(null);
  return (
    <ul className="space-y-2" aria-label="Control room groups">
      {board.groups.map((g) => {
        const style = CONTROL_TONE_STYLES[g.def.tone];
        const count = g.rows.length;
        const expanded = open === g.def.id;
        return (
          <li key={g.def.id} data-group={g.def.id} className={`rounded-xl border ${count ? style.panel : 'bg-surface border-slate-200'}`}>
            <button
              type="button"
              onClick={() => setOpen(expanded ? null : g.def.id)}
              aria-expanded={expanded}
              disabled={count === 0}
              className="touch-target w-full flex items-center gap-3 px-3 py-2.5 text-left disabled:cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 rounded-xl"
              aria-label={`${g.def.label}: ${count}`}
            >
              <span className={`text-3xl font-bold tabular-nums leading-none w-12 text-right ${count ? style.count : 'text-slate-400'}`}>{count}</span>
              <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0 ${count ? style.badge : 'bg-slate-100 text-slate-500'}`} aria-hidden="true">
                <g.def.Icon />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-slate-900">{g.def.label}</span>
                <span className="block text-xs text-slate-600 truncate">
                  {g.subs.length ? g.subs.map((s) => `${s.label} ${s.rows.length}`).join(' · ') : g.def.hint}
                </span>
              </span>
              {count > 0 && (expanded ? <FaChevronDown className="text-slate-500" aria-hidden="true" /> : <FaChevronRight className="text-slate-500" aria-hidden="true" />)}
            </button>
            {expanded && (
              <div className="px-2.5 pb-2.5">
                <GroupPanelBody view={g} now={g.def.id === 'maintenance' ? liveNow : now} onOpenRow={onOpenRow} onOpenGroup={onOpenGroup} onTruckAction={onTruckAction} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** A phone group's trucks: its sub-groups' chips (up to 20), then "Show in the list". */
function GroupPanelBody({
  view,
  now,
  onOpenRow,
  onOpenGroup,
  onTruckAction,
}: {
  view: ControlGroupView;
  now: number;
  onOpenRow: ControlRoomProps['onOpenRow'];
  onOpenGroup: ControlRoomProps['onOpenGroup'];
  onTruckAction?: OnTruckAction;
}) {
  const limit = 20;
  const parts = view.subs.length ? view.subs : [{ key: '', label: '', rows: view.rows }];
  let shown = 0;
  return (
    <div className="space-y-2">
      {parts.map((p) => {
        const room = Math.max(0, limit - shown);
        const list = p.rows.slice(0, room);
        shown += list.length;
        return (
          <div key={p.key || 'all'} className="space-y-1.5">
            {p.label && (
              <div className="flex items-center justify-between px-1 text-sm font-semibold text-slate-800">
                <span className="truncate">{p.label}</span>
                <span className="tabular-nums">{p.rows.length}</span>
              </div>
            )}
            <ul className="space-y-1.5">
              {list.map((pr) => (
                <li key={pr.row.asset_key}>
                  <ChipSlot pr={pr} group={view.def.id} now={now} wall={false} onOpen={onOpenRow} onTruckAction={onTruckAction} side />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
      <button
        type="button"
        onClick={() => onOpenGroup(view.def.id)}
        className="touch-target w-full rounded-lg border border-slate-300 bg-surface px-3 text-sm font-semibold text-slate-800"
      >
        Show {view.rows.length > limit ? `all ${view.rows.length}` : 'them'} in the list
      </button>
    </div>
  );
}

/** Keep the screen awake while the wall display shows (where the browser allows it). */
function useWakeLock(enabled: boolean): void {
  const lock = useRef<{ release: () => Promise<void> } | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    const nav = navigator as Navigator & { wakeLock?: { request: (t: 'screen') => Promise<{ release: () => Promise<void> }> } };
    let cancelled = false;
    const acquire = () => {
      if (!nav.wakeLock || document.visibilityState !== 'visible') return;
      nav.wakeLock
        .request('screen')
        .then((l) => {
          if (cancelled) l.release().catch(() => {});
          else lock.current = l;
        })
        .catch(() => {});
    };
    acquire();
    document.addEventListener('visibilitychange', acquire);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', acquire);
      lock.current?.release().catch(() => {});
      lock.current = null;
    };
  }, [enabled]);
}

export default function ControlRoom({
  rows,
  now,
  scope,
  options,
  onScopeChange,
  onOpenRow,
  onOpenGroup,
  fetchedAt,
  error,
  connection,
  loading,
  phone = false,
  wall = false,
  wallTheme = 'dark',
  onWallThemeChange,
  onEnterWall,
  onExitWall,
  onTruckAction,
}: ControlRoomProps) {
  const liveNow = useSecondClock(true);
  // The board's clock for the groups (cheap re-renders); the second clock for the strip and
  // the maintenance timers. Never behind the board's own clock.
  const boardNow = Math.max(now, 0);
  const board = useMemo(() => buildControlBoard(rows, boardNow), [rows, boardNow]);
  useWakeLock(wall);

  useEffect(() => {
    if (!wall || !onExitWall) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.fullscreenElement) onExitWall();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [wall, onExitWall]);

  // Before the first load the groups would all say 0 — a wrong picture: say it is loading.
  const firstLoad = fetchedAt === null && rows.length === 0;
  const loadingNote = firstLoad ? (
    <div className="rounded-xl border border-dashed border-slate-300 bg-surface px-4 py-10 text-center" role="status">
      <p className={`${wall ? 'text-2xl' : 'text-sm'} font-medium text-slate-700`}>
        {loading || !error ? 'Loading the control room…' : 'The control room could not load. It tries again every 30 seconds.'}
      </p>
    </div>
  ) : null;

  if (phone) {
    return (
      <div className="space-y-3" data-control-room="phone">
        <TotalsStrip totals={board.totals} ready={!firstLoad} now={liveNow} fetchedAt={fetchedAt} error={error} connection={connection} wall={false} phone />
        {loadingNote || <PhoneStack board={board} now={boardNow} liveNow={liveNow} onOpenRow={onOpenRow} onOpenGroup={onOpenGroup} onTruckAction={onTruckAction} />}
      </div>
    );
  }

  if (wall) {
    const scopeLabel = scopeText(scope, options);
    return (
      <div
        data-theme={wallTheme}
        data-control-room="wall"
        className="fixed inset-0 z-[9500] flex flex-col gap-4 p-5 bg-slate-50 text-slate-900 overflow-hidden"
        style={{ colorScheme: wallTheme }}
        role="region"
        aria-label="Control room wall display"
      >
        <TotalsStrip
          lead={
            <div className="flex flex-col justify-center min-w-0 max-w-[15rem] 2xl:max-w-[22rem] px-4 2xl:px-6 py-3">
              <h1 className="text-2xl 2xl:text-3xl font-bold leading-tight text-slate-900">Control room</h1>
              <p className="truncate text-sm 2xl:text-base text-slate-600">{scopeLabel || 'Every truck and loader you can see'}</p>
            </div>
          }
          trail={
            <div className="flex flex-col gap-2 px-4">
              {onWallThemeChange && (
                <button
                  type="button"
                  onClick={() => onWallThemeChange(wallTheme === 'dark' ? 'light' : 'dark')}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-surface text-slate-700 ring-1 ring-slate-300 text-sm font-medium hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  {wallTheme === 'dark' ? <FaSun aria-hidden="true" /> : <FaMoon aria-hidden="true" />}
                  {wallTheme === 'dark' ? 'Light' : 'Dark'}
                </button>
              )}
              {onExitWall && (
                <button
                  type="button"
                  onClick={onExitWall}
                  className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-surface text-slate-700 ring-1 ring-slate-300 text-sm font-medium hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                >
                  <FaCompress aria-hidden="true" /> Exit
                </button>
              )}
            </div>
          }
          totals={board.totals}
          ready={!firstLoad}
          now={liveNow}
          fetchedAt={fetchedAt}
          error={error}
          connection={connection}
          wall
        />
        {loadingNote || <GroupGrid board={board} now={boardNow} liveNow={liveNow} wall onOpenRow={onOpenRow} onOpenGroup={onOpenGroup} />}
      </div>
    );
  }

  return (
    <div className="space-y-4" data-control-room="desktop">
      <TotalsStrip totals={board.totals} ready={!firstLoad} now={liveNow} fetchedAt={fetchedAt} error={error} connection={connection} wall={false} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ScopeBar scope={scope} options={options} onChange={onScopeChange} />
        {onEnterWall && (
          <button
            type="button"
            onClick={onEnterWall}
            className="inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border border-slate-300 bg-surface text-sm font-medium text-slate-700 hover:border-slate-400 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <FaExpand size={12} aria-hidden="true" />
            Wall display
          </button>
        )}
      </div>
      {loadingNote || (
        <GroupGrid board={board} now={boardNow} liveNow={liveNow} wall={false} onOpenRow={onOpenRow} onOpenGroup={onOpenGroup} onTruckAction={onTruckAction} />
      )}
    </div>
  );
}

/** The table / kanban's reminder of a control-room scope, with a way out. */
export function ScopeChip({ scope, options, onClear }: { scope: ControlScope; options: ScopeOptions; onClear: () => void }) {
  if (!scopeIsSet(scope)) return null;
  const text = scopeText(scope, options);
  return (
    <button
      type="button"
      onClick={onClear}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-primary-400 bg-surface text-sm font-medium text-slate-800 hover:bg-slate-50"
      aria-label={`Showing only ${text}. Show every truck`}
    >
      {text}
      <FaTimes size={10} aria-hidden="true" />
    </button>
  );
}
